<template>
  <div class="calendar d-flex flex-column align-items-center">
    <div class="calendar-controls d-flex">
      <button class="btn btn-control" :disabled="disabled || months[0].month === focus.month" @click="subMonth">
        <img :src="$icon('caret-down')">
      </button>
      <div class="select-control">
        <select :style="{ 'background-image': `url(${$icon('caret-down')})` }" :disabled="disabled" v-model="focusModel">
          <option v-for="(month, i) in months" :key="i" :value="{ month: month.month, year: month.year }">{{ month.display }}</option>
        </select>
      </div>
      <button class="btn btn-control" :disabled="disabled || months[months.length - 1].month === focus.month" @click="addMonth">
        <img :src="$icon('caret-down')">
      </button>
    </div>
    <div class="calendar-grid">
      <div class="calendar-row">
        <span v-for="(name, i) in weekdays" :key="i" class="calendar-day is-header">
          {{ name }}
        </span>
      </div>
      <div v-for="(week, i) in grid" :key="i" class="calendar-row">
        <span
          v-for="(day, j) in week"
          :key="j"
          class="calendar-day"
          :class="{
            'is-after': day.after,
            'is-before': day.before,
            'is-weekday': day.weekday < 6,
            'is-weekend': day.weekday > 5,
            'is-saturday': day.weekday === 6,
            'is-sunday': day.weekday === 7,
            'is-disabled': disabled || day.today || day.disabled || (day.weekday === 7 && garage && garage.opened.sun === false) || (day.weekday === 6 && garage && !garage.opened.sat) || (day.weekday < 6 && garage && garage.opened.week === false) || (garage.booking_enabled && !onlineBookings.includes(day.weekday)),
            'is-today': day.today,
            'is-selected': focus.day && focus.day.unix === day.unix
          }"
          @click="makeActive(day)"
        >
          {{ day.date }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DatePicker',
  props: {
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    garage: {
      type: Object,
      required: false,
      default: null
    },
    model: {
      required: false,
      validator: v => typeof v === 'string' || v === null,
      default: null
    }
  },
  data () {
    return {
      focus: {
        day: null,
        month: moment().month(),
        year: moment().year()
      },
      onlineBookings: []
    }
  },
  watch: {
    garage: function() {
      this.focus.day = null;
      this.onlineBookings = [];
      let i = 1;
      for(const day in this.garage.booking_days) {
        if(this.garage.booking_days[day]) {
          this.onlineBookings.push(i);
        }

        i++;
      }
    }
  },
  computed: {
    focusModel: {
      get () {
        return {
          month: this.focus.month,
          year: this.focus.year
        }
      },
      set (val) {
        this.focus.month = val.month
        this.focus.year = val.year
      }
    },
    grid () {
      const startOfMonth = moment([this.focus.year, this.focus.month, 1]).startOf('month')
      const endOfMonth = moment([this.focus.year, this.focus.month, 1]).endOf('month')
      const grid = {}

      let now = startOfMonth.clone()
      
      let lw = now.week()
      while (now.month() === endOfMonth.month()) {        
        let week = now.week()
        if (lw > 1 && week === 1) {
          week = lw + 1
        }

        lw = week

        if (!grid[week]) {
          grid[week] = []
        }

        grid[week].push(this.makeDay(now))

        now.add(1, 'days')
      }

      const weeks = Object.keys(grid)
      if (grid[weeks[0]][0].weekday > 0) {
        const tmp = []
        for (let i = grid[weeks[0]][0].weekday - 1; i > 0; i--) {
          const n = startOfMonth.clone().subtract(i, 'days')
          
          tmp.push(this.makeDay(n))
        }

        grid[weeks[0]] = [...tmp, ...grid[weeks[0]]]
      }

      const lastWeek = weeks[weeks.length - 1]
      const lastDay = grid[lastWeek][grid[lastWeek].length - 1]
      if (lastDay.weekday < 7) {
        const tmp = []
        for (let i = 1; i <= 7 - lastDay.weekday; i++) {
          const n = endOfMonth.clone().add(i, 'days')
          
          tmp.push(this.makeDay(n))
        }

        grid[lastWeek] = [...grid[lastWeek], ...tmp]
      }

      if (weeks.length < 6) {
        const lastDay = moment.unix(grid[lastWeek][grid[lastWeek].length - 1].unix)
        
        grid[lastWeek + 1] = []
        for (let i = 0; i < 7; i++) {
          lastDay.add(1, 'days')

          grid[lastWeek + 1].push(this.makeDay(lastDay))
        }
      }

      if (weeks.length > 6) {
        grid[weeks[weeks.length - 2]] = [...grid[weeks[weeks.length - 2]], ...grid[weeks[weeks.length - 1]]]
        delete grid[weeks[weeks.length - 1]]
      }

      return grid
    },
    months () {
      moment.locale('pl')
      const months = []
      const m = moment()

      const min = m.month()
      const max = min + 7

      for (let i = min; i < max; i++) {        
        const display = m.format('MMMM')

        months.push({
          id: i,
          display: `${display.charAt(0).toUpperCase() + display.slice(1)} ${m.year()}`,
          month: m.month(),
          unixStart: m.startOf('month').unix(),
          unixEnd: m.endOf('month').unix(),
          year: m.year()
        })

        m.add(1, 'months')
      }

      return months
    },
    weekdays () {
      moment.locale('pl')
      const startOfWeek = moment().startOf('week')
      const days = []

      let now = startOfWeek.clone()
      while (now.week() === startOfWeek.week()) {
        days.push(
          now.format('dd')
        )
        now.add(1, 'days')
      }

      return days
    }
  },
  methods: {
    addMonth () {
      if (this.focus.month === 11) {
        this.focus.year++
        this.focus.month = 0
      } else if (this.focus.month !== this.months[this.months.length - 1].month) {
        this.focus.month++
      }
    },
    subMonth () {
      if (this.focus.month === 0) {
        this.focus.year--
        this.focus.month = 11
      } else if (this.focus.month !== this.months[0].month) {
        this.focus.month--
      }
    },
    makeActive (day) {
      if (
          (day.weekday === 7 && this.garage && this.garage.opened.sun === false) || 
          (day.weekday === 6 && this.garage && !this.garage.opened.sat) || 
          (day.weekday < 6 && this.garage && this.garage.opened.week === false)  || 
          (this.garage.booking_enabled && !this.onlineBookings.includes(day.weekday))
      ) { 
        return
      }

      if (!this.disabled && !day.disabled && this.isValidDay(day)) {
        if (day.after) {
          this.addMonth()
        }

        if (day.before) {
          this.subMonth()
        }
        
        this.focus.day = day
        this.focus.year = day.year

        this.$emit('update:model', moment.unix(day.unix).format('YYYY-MM-DD'))
      }
    },
    addWeekdays(date, days) {
      date = moment(date);
      while (days > 0) {
        date = date.add(1, 'days');
        // decrease "days" only if it's a weekday.
        if (date.isoWeekday() !== 6 && date.isoWeekday() !== 7) {
          days -= 1;
        }
      }

      return date;
    },
    makeDay (m) {
      const now = moment([this.focus.year, this.focus.month, 1]).startOf('day')
      const timeBuffer = this.garage && this.garage.time_buffer ? this.garage.time_buffer : 0
      const cloned = m.clone()

      if(m.unix() < this.addWeekdays(moment(), timeBuffer).startOf('day').unix()) {
        this.addWeekdays(moment(), timeBuffer).startOf('day').format('YYYY-MM-DD')
      }

      return {
        after: cloned.startOf('month').unix() > now.endOf('month').unix(),
        before: cloned.endOf('month').unix() < now.startOf('month').unix(),
        date: m.date(),
        disabled: m.unix() <= this.addWeekdays(moment(), timeBuffer).startOf('day').unix(),
        month: m.month(),
        unix: m.startOf('day').unix(),
        today: m.year() === moment().year() && m.week() === moment().week() && m.day() === moment().day(),
        weekday: m.isoWeekday(),
        year: m.year()
      }
    },
    isValidDay (day) {
      const fMonth = this.months[0]
      const lMonth = this.months[this.months.length - 1]

      return day.unix >= fMonth.unixStart &&
        day.unix <= lMonth.unixEnd
    }
  }
}
</script>